import { createAuthorizedConsumer } from "../../../collaboration/transport";
import React, { useEffect, useState, useRef } from "react";
import { PhoneIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import RoleOnly from "../../../routes/roleOnly";

const CallNotification = ({ companyId }) => {
  const [currentCall, setCurrentCall] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  const handleEventRef = useRef(null);
  useEffect(() => {
    if (!companyId) return;
    let active = true, cable, subscription, timer, recovering = false, attempts = 0;
    const connect = async () => {
      recovering = false;
      try {
        cable = await createAuthorizedConsumer({ channel: 'phonero' });
        if (!active) { cable.disconnect(); return; }
        subscription = cable.subscriptions.create({ channel: 'PhoneroCallsChannel' }, {
          received: (data) => { if (active) handleEventRef.current?.(data); },
          connected: () => { if (active) { setWsConnected(true); attempts = 0; } },
          disconnected: () => recover(),
          rejected: () => recover(),
        });
      } catch (error) {
        if (!active) return;
        setWsConnected(false);
        if (![401, 403, 404].includes(error?.response?.status)) recover();
      }
    };
    const recover = () => {
      if (!active || recovering) return;
      recovering = true;
      setWsConnected(false);
      cable?.disconnect();
      timer = setTimeout(connect, Math.min(30000, 1000 * (2 ** Math.min(attempts++, 5))));
    };
    connect();
    return () => { active = false; clearTimeout(timer); subscription?.unsubscribe(); cable?.disconnect(); };
  }, [companyId]);

  const handleCallEvent = (data) => {
    const { event_type, data: callData } = data;

    switch (event_type) {
      case "call_started":
        setCurrentCall(callData);
        setIsVisible(true);
        showCallNotification(callData, "Incoming call");
        break;
      case "call_answered":
        if (currentCall?.call_id === callData.call_id) {
          setCurrentCall((prev) => ({ ...prev, status: "answered" }));
        }
        break;
      case "call_ended":
        if (currentCall?.call_id === callData.call_id) {
          setCurrentCall((prev) => ({ ...prev, status: "ended" }));
          setTimeout(() => {
            setIsVisible(false);
            setCurrentCall(null);
          }, 3000);
        }
        break;
      case "call_missed":
        showCallNotification(callData, "Missed call");
        break;
      default:
        break;
    }
  };

  handleEventRef.current = handleCallEvent;

  const showCallNotification = (callData, title) => {
    const customerName = callData.customer?.name || "Unknown";
    const phoneNumber = callData.from_number || "Unknown number";

    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? "animate-enter" : "animate-leave"
          } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
        >
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <PhoneIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-500">{customerName}</p>
                <p className="mt-1 text-sm text-gray-500">{phoneNumber}</p>
              </div>
            </div>
          </div>
          <div className="flex border-l border-gray-200">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      ),
      {
        duration: 10000,
      }
    );
  };

  if (!isVisible || !currentCall) {
    return null;
  }

  return (
    <RoleOnly role="phone" action="read">
      <div className="fixed top-4 right-4 z-50">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PhoneIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {currentCall.status === "answered"
                    ? "Call in progress"
                    : "Incoming call"}
                </p>
                <p className="text-sm text-gray-500">
                  {currentCall.customer?.name || "Unknown caller"}
                </p>
                <p className="text-sm text-gray-500">
                  {currentCall.from_number}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsVisible(false)}
              className="ml-4 flex-shrink-0"
            >
              <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            </button>
          </div>

          {currentCall.customer && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="text-xs text-gray-500">
                <p>
                  <strong>Customer:</strong> {currentCall.customer.name}
                </p>
                {currentCall.customer.customer_number && (
                  <p>
                    <strong>Customer #:</strong>{" "}
                    {currentCall.customer.customer_number}
                  </p>
                )}
                {currentCall.customer.email && (
                  <p>
                    <strong>Email:</strong> {currentCall.customer.email}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* WebSocket connection status indicator */}
          {!wsConnected && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <div className="text-xs text-yellow-600">
                <p>⚠️ Real-time updates not available</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </RoleOnly>
  );
};

export default CallNotification;
